import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";

const STATUS_LABELS = { lead: "Lead", prospect: "Prospect", buyer: "Buyer" };
const STATUS_COLOR = {
  lead: "bg-amber-50 text-amber-700 border-amber-200",
  prospect: "bg-blue-50 text-blue-700 border-blue-200",
  buyer: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const EMPTY = { name: "", email: "", phone: "", mobile: "", address: "", postal_address: "", license_number: "", license_version: "", gender: "", status: "lead", preferred_type: "", notes: "" };
const inputCls = "w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500";

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/customers", { params: { search: search || undefined } });
    setRows(data);
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); };
  const save = async () => {
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success(editId ? "Customer updated" : "Customer added");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Customers</h1>
          <p className="text-sm text-slate-500 mt-1">{rows.length} contacts in your CRM.</p>
        </div>
        <button onClick={openNew} data-testid="customer-add-button" className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Customer
        </button>
      </div>

      <div className="card-elevated p-4 relative">
        <Search className="w-4 h-4 absolute left-7 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="customer-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone…" className="w-full h-10 pl-10 pr-4 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-3 pl-4">Name</th>
              <th className="text-left">Contact</th>
              <th className="text-left">Location</th>
              <th className="text-left">Preference</th>
              <th className="text-left">Status</th>
              <th className="pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-16 text-center text-slate-400">No customers yet.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-slate-100 row-hover">
                <td className="py-3 pl-4 font-semibold text-slate-900">{c.name}</td>
                <td>
                  <div className="text-slate-700">{c.email || "—"}</div>
                  <div className="text-xs text-slate-500 font-mono">{c.phone || "—"}</div>
                </td>
                <td className="text-slate-600">{c.address || "—"}</td>
                <td className="text-slate-600">{c.preferred_type || "—"}</td>
                <td><span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded border ${STATUS_COLOR[c.status]}`}>{STATUS_LABELS[c.status]}</span></td>
                <td className="pr-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(c)} className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(c.id)} className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{editId ? "Edit customer" : "Add customer"}</h3>
            <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              <div className="col-span-2"><label className="text-xs font-semibold uppercase text-slate-600">Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls + " mt-1"} /></div>
              <div><label className="text-xs font-semibold uppercase text-slate-600">Mobile</label><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inputCls + " mt-1 font-mono"} /></div>
              <div><label className="text-xs font-semibold uppercase text-slate-600">Email</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls + " mt-1"} /></div>
              <div className="col-span-2"><label className="text-xs font-semibold uppercase text-slate-600">Physical address</label><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls + " mt-1"} /></div>
              <div className="col-span-2"><label className="text-xs font-semibold uppercase text-slate-600">Postal address</label><input value={form.postal_address} onChange={(e) => setForm({ ...form, postal_address: e.target.value })} className={inputCls + " mt-1"} /></div>
              <div><label className="text-xs font-semibold uppercase text-slate-600">Licence #</label><input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value.toUpperCase() })} className={inputCls + " mt-1 font-mono"} /></div>
              <div><label className="text-xs font-semibold uppercase text-slate-600">Licence version</label><input value={form.license_version} onChange={(e) => setForm({ ...form, license_version: e.target.value })} className={inputCls + " mt-1 font-mono"} /></div>
              <div><label className="text-xs font-semibold uppercase text-slate-600">Gender</label>
                <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className={inputCls + " mt-1"}>
                  <option value="">Prefer not to say</option><option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
              <div><label className="text-xs font-semibold uppercase text-slate-600">Preferred type</label><input value={form.preferred_type} onChange={(e) => setForm({ ...form, preferred_type: e.target.value })} className={inputCls + " mt-1"} placeholder="SUV, Sedan…" /></div>
              <div className="col-span-2"><label className="text-xs font-semibold uppercase text-slate-600">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls + " mt-1"}>
                  <option value="lead">Lead</option><option value="prospect">Prospect</option><option value="buyer">Buyer</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setOpen(false)} className="h-10 px-4 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold">Cancel</button>
              <button onClick={save} data-testid="customer-save-button" className="h-10 px-4 bg-blue-600 text-white rounded-lg text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
